import { memo, useCallback, useEffect, useState, type FormEvent } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

type PaginationProps = {
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  totalCount: number;
  isLoading?: boolean;
};

const PageButton = memo(function PageButton({
  page,
  isLoading,
  onGoTo,
}: {
  page: number;
  isLoading: boolean;
  onGoTo: (p: number) => void;
}) {
  const handleClick = useCallback(() => onGoTo(page), [page, onGoTo]);
  return (
    <button
      type="button"
      disabled={isLoading}
      onClick={handleClick}
      className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 inset-ring inset-ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {page}
    </button>
  );
});

function getPageItems(current: number, totalPages: number): (number | 'ellipsis')[] {
  // Aim: max ~7 tokens: 1 ... (window) ... last
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set<number>();

  // Always show ends
  pages.add(1);
  pages.add(totalPages);

  // Core window around current
  pages.add(current);
  pages.add(current - 1);
  pages.add(current + 1);

  // When near the start, show a bit more at the beginning
  if (current <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }

  // When near the end, show a bit more at the end
  if (current >= totalPages - 2) {
    pages.add(totalPages - 1);
    pages.add(totalPages - 2);
    pages.add(totalPages - 3);
  }

  // Clamp valid range
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);

  // Insert ellipses for gaps
  const items: (number | 'ellipsis')[] = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const p = sorted[i];
    const prev = sorted[i - 1];

    if (i > 0 && prev != null && p - prev > 1) {
      items.push('ellipsis');
    }
    items.push(p);
  }

  return items;
}

export function Pagination({ page, setPage, totalCount, pageSize, isLoading = false }: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  const [goToPageValue, setGoToPageValue] = useState(String(currentPage));

  const start = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = totalCount === 0 ? 0 : Math.min(totalCount, currentPage * pageSize);
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;
  const pageItems = getPageItems(currentPage, totalPages);

  const arrowsMobileCls =
    'relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer';
  const arrowsCls =
    'relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 inset-ring inset-ring-gray-300 hover:bg-[#274483]/15 focus:z-20 focus:outline-offset-0 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer';

  const handleGoTo = useCallback(
    (p: number) => {
      // Prevent rapid pagination clicks from firing overlapping requests to the backend.
      if (isLoading) return;
      if (p < 1 || p > totalPages || p === currentPage) return;
      setPage(p);
    },
    [isLoading, totalPages, currentPage, setPage],
  );

  const handleGoPrev = useCallback(() => {
    if (canPrev) handleGoTo(currentPage - 1);
  }, [canPrev, handleGoTo, currentPage]);

  const handleGoNext = useCallback(() => {
    if (canNext) handleGoTo(currentPage + 1);
  }, [canNext, handleGoTo, currentPage]);

  useEffect(() => {
    setGoToPageValue(String(currentPage));
  }, [currentPage]);

  const handleGoToSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const parsed = Number(goToPageValue);
    if (!Number.isInteger(parsed)) return;

    const nextPage = Math.min(totalPages, Math.max(1, parsed));
    handleGoTo(nextPage);
    setGoToPageValue(String(nextPage));
  };

  return totalCount > pageSize ? (
    <div className="flex items-center justify-between border-t border-gray-200 pt-6">
      {/* Mobile: simple prev/next */}
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          type="button"
          disabled={!canPrev || isLoading}
          onClick={handleGoPrev}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button type="button" disabled={!canNext || isLoading} onClick={handleGoNext} className={arrowsMobileCls}>
          Next
        </button>
      </div>

      {/* Desktop: full pagination */}
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700" aria-label="pagination-summary">
            Showing <span className="font-medium">{start}</span> to <span className="font-medium">{end}</span> of{' '}
            <span className="font-medium">{totalCount}</span> results
          </p>
        </div>
        <div className="flex items-center gap-3">
          <nav aria-label="Pagination" className="isolate inline-flex -space-x-px rounded-md shadow-xs cursor-pointer">
            <button type="button" disabled={!canPrev || isLoading} onClick={handleGoPrev} className={arrowsCls}>
              <span className="sr-only">Previous</span>
              <ChevronLeftIcon aria-hidden="true" className="size-5" />
            </button>

            {pageItems.map((item, idx) => {
              if (item === 'ellipsis') {
                return (
                  <span
                    key={`ellipsis-${idx}`}
                    className="relative inline-flex cursor-default select-none items-center px-4 py-2 text-sm font-semibold text-gray-500 inset-ring inset-ring-gray-300 focus:outline-offset-0"
                  >
                    ...
                  </span>
                );
              }

              const p = item;
              const isCurrent = p === currentPage;

              if (isCurrent) {
                return (
                  <span
                    key={p}
                    aria-current="page"
                    className="relative z-10 inline-flex items-center bg-[#274483] px-4 py-2 text-sm font-semibold text-white focus:z-20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#274483]"
                  >
                    {p}
                  </span>
                );
              }

              return <PageButton key={p} page={p} isLoading={isLoading} onGoTo={handleGoTo} />;
            })}

            <button
              type="button"
              disabled={!canNext || isLoading}
              onClick={handleGoNext}
              className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 inset-ring inset-ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <span className="sr-only">Next</span>
              <ChevronRightIcon aria-hidden="true" className="size-5" />
            </button>
          </nav>

          <form onSubmit={handleGoToSubmit} className="flex items-center gap-2">
            <label htmlFor="pagination-go-to-page" className="sr-only">
              Go to page
            </label>
            <input
              id="pagination-go-to-page"
              type="number"
              min={1}
              max={totalPages}
              step={1}
              inputMode="numeric"
              value={goToPageValue}
              onChange={(event) => setGoToPageValue(event.target.value)}
              disabled={isLoading}
              aria-label="Go to page"
              className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700 focus:border-[#274483] focus:outline-none focus:ring-1 focus:ring-[#274483] disabled:cursor-not-allowed disabled:opacity-40"
            />
            <button
              type="submit"
              disabled={isLoading || !goToPageValue.trim()}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Go
            </button>
          </form>
        </div>
      </div>
    </div>
  ) : null;
}
